<template>
  <modal titulo="Odin - Análise da Situação Jurídica" id="odin-situacao-juridica"
         :fechar="true" @close="close" :loading="loading"  >
    <template #default>
      <div id="dados-principais">
        <auditoria-details-content
          :selecionar="true"
          @selecionados="setSelecionados"
          title-antes="Situação Atual"
          title-depois="Situação Jurídica Extraída"
          :auditoria="dados" :formatters="formatters" v-if="!loading" />
      </div>
    </template>

    <template #footer>
      <button class="btn btn-outline-secondary mr-auto" type="button" @click.prevent="close(false)">
        Fechar
      </button>
      <v-button class="btn-outline-primary" :run="salvar" :popover="true" v-if="selecionados.length">
        Atualizar
      </v-button>
    </template>
  </modal>
</template>

<script setup>

import {computed, getCurrentInstance, onMounted, ref} from 'vue';
import Modal from '@/components/Modal.vue';
import FrontBusiness from "@/business/FrontBusiness";
import IndicadorRealBusiness from "@/business/indicadores/IndicadorRealBusiness";
import AuditoriaDetailsContent from "@/v2/components/Auditoria/AuditoriaDetailsContent.vue";
import OdinBusiness from "@/views/Odin/OdinBusiness";
import Utils from "@/commons/Utils";

const props = defineProps({
  indicadorRealId: String,
});

onMounted(() => {
  load();
});

// Variávei
const loading = ref(true)
const indicador = ref({})
const atos = ref([])
const formatters = ref([])
const selecionados = ref([])
const dados = ref({ antes: {}, depois: {} })

// Config
const { proxy } = getCurrentInstance()


// computed
const restricoes = computed(() => {
  const atuais = (indicador.value.restricoes || []).map(r => ({
    nome: r?.nome,
    atual: {
      id: r.id,
      indicadorPessoalId: r?.indicadorPessoalId,
      nome: r?.nome,
      restricao: r?.restricao,
      papel: r?.papel,
      documento: r?.documento,
      descricaoRestricao: r?.descricaoRestricao ? JSON.parse(r?.descricaoRestricao) : ""
    }
  }))
  let ato = atos.value.at(-1);
  const extraidosTemp = [...(ato.situacaoJuridica.pessoas || []).filter(e => e.papel !== 'PROPRIETARIO')]

  atuais.forEach(pessoa => {
    const idx = extraidosTemp.findIndex(ext =>
      Utils.extrairNumeros(pessoa.atual.documento) &&
      Utils.extrairNumeros(pessoa.atual.documento) === Utils.extrairNumeros(ext.documento) &&
      OdinBusiness.calcularSimilaridade(pessoa.atual.nome, ext.nome) >= 70 &&
      pessoa.atual.papel === ext.papel
    )
    if (idx !== -1) {
      pessoa.extraido = extraidosTemp.splice(idx, 1)[0]
    }
  })

  atuais.forEach(pessoa => {
    if (!pessoa.extraido) {
      const idx = extraidosTemp.findIndex(ext =>
        OdinBusiness.calcularSimilaridade(pessoa.atual.nome, ext.nome) >= 80
      )
      if (idx !== -1) {
        pessoa.extraido = extraidosTemp.splice(idx, 1)[0]
      }
    }
  })

  atuais.forEach(element => {
    element.ordemAto = element.atual.descricaoRestricao.codigo;
  });

  const leftovers = extraidosTemp.map(ext => ({
    nome: ext.nome,
    atual: null,
    extraido: ext
  }))

  return Utils.orderBy([...atuais, ...leftovers], ['ordemAto', 'nome'], ['asc', 'asc'])
})


// Funções
const close = (retorno) => {
  FrontBusiness.closeModal(retorno);
}

const showError = (e) => {
  FrontBusiness.fullErrorHandler(proxy, e, '')
}

const setSelecionados = (lista) => {
  selecionados.value = lista
}

// Funções ASYNC
const load = async () => {
  atos.value = await IndicadorRealBusiness.analiseSituacaoJuridica(props.indicadorRealId)
    .catch(e => {
      FrontBusiness.showError('', e.message);
      return [];}
    );

  indicador.value = await IndicadorRealBusiness.getById(props.indicadorRealId);

  dados.value = await OdinBusiness.compararAnaliseJuridica(props.indicadorRealId).catch(e => {
    FrontBusiness.showError('', e.message);
    close();
    return;
  });

  if (!dados.value) return;

  let campos =  Object.keys(dados.value.depois)
    .filter(key => !Utils.isEmptyNullOrUndefined(dados.value.depois[key], true));

  let extras = [];

  dados.value.antes.proprietarios.map((e, i) => {
    let id = `proprietario_${i}`
    let d = dados.value.depois.proprietarios[i];
    extras.push({label: e.nome || d.nome, key: id, group: 'Proprietários', formatter: (registros) => {
        return `<p><strong>Documento:</strong> ${registros?.documento || '-'}</p><p><strong>Fração:</strong> ${registros?.fracao || 0}%</p>`
      }})
    dados.value.antes[id] = e;
    dados.value.depois[id] = d;
  })

  restricoes.value.map(value => {
    const descricaoRestricao = value.atual?.descricaoRestricao;
    if (descricaoRestricao && value.extraido){
      value.extraido.descricaoRestricao = descricaoRestricao;
    }
  })

  let restricoesAtuais = {};
  let restricoesAnalise = {};
  restricoes.value.map((e, i) => {
    let id = `restricao_${i}`
    extras.push({
      label: e.nome,
      key: id,
      group: 'Gravames',
      ocultarSelecao: true,
      formatter: (registros) => {
        let codigo = ""
        let sigla = ""
        if(registros?.descricaoRestricao){
          codigo = registros.descricaoRestricao.codigo || "";
          sigla = (codigo == 0 ? 'M' : registros.descricaoRestricao.tipoAto === 'AVERBACAO' ? 'AV' : 'R') || ""
        }
        return `<p><strong>Documento:</strong> ${registros?.documento || '-'}</p><p><strong>Papel:</strong> ${registros?.papel || '-'}</p><p><strong>Restrição:</strong> ${registros?.restricao || '-'}</p><p><strong>Ato:</strong> ${sigla + "." + codigo || "-"}</p>`
      }
    })
    restricoesAtuais[id] = e.atual;
    restricoesAnalise[id] = e.extraido;
    return e;
  })

  dados.value = {
    antes: {...dados.value.antes, ...restricoesAtuais},
    depois: {...dados.value.depois, ...restricoesAnalise},

  };

  formatters.value = [...extras,
    ...OdinBusiness.getFormatters().filter(e => {
      return campos.includes(e.key)
    }).map(e => {
      e.group = 'Dados Gerais'
      if (e.key === 'categoria') e.label = 'Destinação'
      return e
    })
  ];

  loading.value = false;
}

const pick = (obj, allowed) => {
  const allow = new Set(allowed);
  return Object.fromEntries(
    Object.entries(obj).filter(([k]) => allow.has(k))
  );
}

const salvar = async () => {
  let ids = selecionados.value.filter(e => e.startsWith('proprietario_')).map(e => e.replace('proprietario_', ''));
  const result = pick( dados.value.depois, selecionados.value.filter(e => !e.startsWith('proprietario_')));
  result.proprietarios = dados.value.antes.proprietarios || [];
  for (const id in ids) {
    result.proprietarios[id] = dados.value.depois.proprietarios[id];
  }
  let save = await OdinBusiness.salvarAnaliseJuridica(props.indicadorRealId, result).catch(showError);
  if (save) {
    FrontBusiness.showSuccess('', 'Ação realizada com sucesso');
    close(true);
  }
}

</script>

<script>
export default {
  modal: {
    width: 1000,
    escapable: false
  }
}
</script>

<style lang="scss">
#odin-situacao-juridica {
  td {
    vertical-align: middle !important;
  }
}
</style>
